import { c, t, sp, r } from '../../theme';

// The L0 / L1 / L2 switcher.
//
// One page, three modes, rather than three pages: the underlying snapshot is
// identical and only the grouping and panel set change, so three routes would
// mean three copies of the same aggregation drifting apart.
//
// The levels are an audience ladder, not a drill path — L1 is not "inside" L0.
// A site manager lives on L1 and a storeroom clerk on L2, so the switcher is
// styled as peers rather than as breadcrumbs.
// The names carry no cadence word, deliberately. "Executive monthly" and "Site
// monthly" both said the wrong thing: this is a snapshot board refreshed by the
// daily ingest, so L1 is TODAY's position, not last month's -- its own subtitle
// already said "updated daily" directly under a heading that said monthly, and
// a reader asked which one to believe.
//
// The comment that used to sit on L1 explaining why it is not called "weekly"
// was answering the same question from the other side. The cadence is a
// property of the trend panel, which states its own grain; the level is an
// AUDIENCE, and naming it after a frequency is what invited the confusion.
export const LEVELS = [
  {
    key:   'L0',
    label: 'L0',
    name:  'Executive',
    blurb: 'Parts working capital and obsolescence exposure',
  },
  {
    key:   'L1',
    label: 'L1',
    name:  'Site',
    blurb: 'Stock position, ageing and exceptions by cost center — updated daily',
  },
  {
    key:   'L2',
    label: 'L2',
    name:  'Storeroom board',
    blurb: 'This week’s reorder, over-max and disposition worklist',
  },
];

export default function LevelBar({ level, onChange }) {
  const current = LEVELS.find((l) => l.key === level) || LEVELS[1];

  return (
    <div style={s.bar}>
      <style>{css}</style>
      <div style={s.left}>
        <div style={s.crumb}>
          <span style={s.crumbRoot}>MOR</span>
          <span style={s.crumbSep}>›</span>
          <span>Inventory</span>
        </div>
        <div style={s.blurb}>{current.label} · {current.name} — {current.blurb}</div>
      </div>

      <div style={s.right}>
        <span style={s.levelLabel}>Level</span>
        <div style={s.switcher} role="tablist" aria-label="Report level">
          {LEVELS.map((l) => (
            <button
              key={l.key}
              type="button"
              role="tab"
              aria-selected={l.key === level}
              title={`${l.name} — ${l.blurb}`}
              className={`inv-level${l.key === level ? ' inv-level-on' : ''}`}
              onClick={() => onChange(l.key)}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Hover and focus cannot be expressed in the inline style objects the rest of
// the page uses, so this one component carries a real stylesheet — the same
// pattern PageHeader and AppShell use.
const css = `
.inv-level {
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${c.onNavyMuted};
  background: ${c.navyLight};
  border: 1px solid transparent;
  border-radius: ${r.md};
  padding: 5px 16px;
  cursor: pointer;
  transition: background 120ms, color 120ms;
}
.inv-level:hover { color: ${c.onNavy}; background: ${c.navy}; }
.inv-level-on,
.inv-level-on:hover {
  background: ${c.surface};
  color: ${c.navy};
  border-color: ${c.surface};
}
`;

const s = {
  bar: {
    background: c.navy,
    borderRadius: `${r.xl} ${r.xl} 0 0`,
    padding: `${sp[4]} ${sp[5]}`,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: sp[4], flexWrap: 'wrap',
  },
  left:       { minWidth: 0 },
  crumb:      { ...t.h3, color: c.onNavy, display: 'flex', alignItems: 'center', gap: sp[2] },
  crumbRoot:  { color: c.onNavyMuted },
  crumbSep:   { color: c.onNavyLabel },
  blurb:      { ...t.micro, color: c.onNavyFaint, marginTop: '3px' },
  right:      { display: 'flex', alignItems: 'center', gap: sp[3] },
  levelLabel: { ...t.label, color: c.onNavyLabel },
  switcher:   { display: 'flex', gap: sp[1] },
};
